import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/admin';
import PlanetCanvas from '../planet/PlanetCanvas';
import { Globe, ArrowLeft, Coins, BarChart3, User, Calendar } from 'lucide-react';

interface AdminPlanetViewerProps {
  planetId: string;
  onClose: () => void;
}

export default function AdminPlanetViewer({ planetId, onClose }: AdminPlanetViewerProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'planet', planetId],
    queryFn: () => adminApi.getPlanet(planetId),
  });

  const detail = data?.data;
  const planet = detail?.planet as Record<string, any> | undefined;
  const balance = detail?.balance ?? 0;

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950">
        <div className="text-cyan-400 animate-pulse">Loading planet...</div>
      </div>
    );
  }

  if (!planet) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950">
        <div className="text-red-400">Planet not found</div>
      </div>
    );
  }

  const stage = planet.stage || 0;
  const visualState = (planet?.visual_state as Record<string, unknown>) || {};
  const surfaceItems = (planet?.surface_items as Array<{
    item_id: string;
    angle: number;
    altitude: number;
    layer: string;
    scale: number;
    visible?: boolean;
    upgrade?: { category: string; name: string } | null;
  }>) || [];
  const visibleSurfaceItems = surfaceItems.filter((item) => item.visible !== false);
  const state = (planet?.state as Record<string, unknown>) || {};
  const eventHistory = (state.eventHistory as Array<Record<string, unknown>>) || [];
  const planetConfig = (visualState as Record<string, unknown>)?.planetConfig as any;
  const orbitEpochMs = planet?.created_at ? Date.parse(planet.created_at) : undefined;

  const stageNames = [
    'Barren Planet',
    'Terraforming',
    'Spark of Life',
    'Plant Life',
    'Complex Life',
    'Civilisation',
    'Technology',
    'Space Technology',
    'Space Exploration',
  ];

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-950">
      {/* Header bar */}
      <header className="flex items-center justify-between gap-3 bg-slate-900 border-b border-slate-800 px-4 py-3 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Admin
          </button>
          <div className="h-4 w-px bg-slate-700 hidden sm:block" />
          <div className="flex items-center gap-2 min-w-0">
            <Globe className="w-4 h-4 text-cyan-400 shrink-0" />
            <span className="text-sm font-semibold text-white truncate">{planet.name}</span>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-400">
            <User className="w-3 h-3" />
            <span className="truncate">{planet.user?.name}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-800 rounded-full">
            <BarChart3 className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-xs text-slate-300">Stage {stage}</span>
            <span className="hidden sm:inline text-[10px] text-slate-500">{stageNames[stage] || ''}</span>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-800 rounded-full">
            <Coins className="w-3.5 h-3.5 text-yellow-400" />
            <span className="text-xs font-mono text-slate-300">{balance.toLocaleString()}</span>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 bg-slate-800 rounded-full">
            <Calendar className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-xs text-slate-400">Day {planet?.current_day || 1}</span>
          </div>
        </div>
      </header>

      {/* Canvas area */}
      <div className="flex-1 relative overflow-hidden">
        <PlanetCanvas
          stage={stage}
          visualState={visualState as Record<string, unknown>}
          surfaceItems={visibleSurfaceItems}
          eventHistory={eventHistory}
          planetConfig={planetConfig}
          planetId={planet.planet_id}
          orbitEpochMs={orbitEpochMs}
          dayDurationMs={planet?.day_duration_ms}
        />
      </div>
    </div>
  );
}
